import React, { useContext, useEffect, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { format, isSameDay, parseISO } from "date-fns";
import clsx from "clsx";

import { makeStyles } from "@material-ui/core/styles";
import ListItem from "@material-ui/core/ListItem";
import ListItemAvatar from "@material-ui/core/ListItemAvatar";
import ListItemText from "@material-ui/core/ListItemText";
import Typography from "@material-ui/core/Typography";
import Badge from "@material-ui/core/Badge";
import Divider from "@material-ui/core/Divider";
import IconButton from "@material-ui/core/IconButton";
import VisibilityIcon from "@material-ui/icons/Visibility";
import CloseIcon from "@material-ui/icons/Close";
import { Tooltip } from "@material-ui/core";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";
import ConnectionChannelBadge from "../ConnectionChannelBadge";
import ContactAvatar from "../ContactAvatar";

const agentNameCache = {};
const pendingClosePermissionCache = {
  loaded: false,
  value: false,
  request: null
};

const useStyles = makeStyles(theme => ({
  ticket: {
    position: "relative",
    margin: "8px 10px",
    padding: "10px 12px 10px 30px",
    borderRadius: 18,
    alignItems: "flex-start",
    background:
      theme.palette.type === "dark"
        ? "linear-gradient(180deg, rgba(20,35,31,0.98) 0%, rgba(15,28,25,0.98) 100%)"
        : "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.98) 100%)",
    boxShadow:
      theme.palette.type === "dark"
        ? "0 10px 24px rgba(2, 6, 23, 0.24)"
        : "0 10px 24px rgba(15, 23, 42, 0.08)",
    border: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(52, 211, 153, 0.10)"
        : "rgba(148, 163, 184, 0.16)"
    }`,
    transition: "transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease",
    "&:hover": {
      transform: "translateY(-1px)",
      boxShadow: "0 14px 28px rgba(15, 23, 42, 0.12)",
      borderColor:
        theme.palette.type === "dark"
          ? "rgba(52, 211, 153, 0.18)"
          : "rgba(18, 140, 126, 0.22)"
    },
    "&.Mui-selected": {
      background:
        theme.palette.type === "dark"
          ? "linear-gradient(180deg, rgba(16,70,60,0.98) 0%, rgba(13,56,48,0.96) 100%)"
          : "linear-gradient(180deg, rgba(232,248,242,0.98) 0%, rgba(223,243,236,0.96) 100%)",
      borderColor:
        theme.palette.type === "dark"
          ? "rgba(52, 211, 153, 0.28)"
          : "rgba(18, 140, 126, 0.28)",
      boxShadow:
        theme.palette.type === "dark"
          ? "0 14px 30px rgba(2, 6, 23, 0.28)"
          : "0 14px 30px rgba(18, 140, 126, 0.16)"
    }
  },
  pendingTicket: {
    cursor: "unset"
  },
  pendingTicketPreviewEnabled: {
    cursor: "pointer"
  },
  contactNameWrapper: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    width: "100%"
  },
  primaryTextWrap: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    minWidth: 0,
    flex: 1
  },
  agentName: {
    fontSize: "0.8rem",
    fontWeight: 600,
    color: theme.palette.text.secondary,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: 150
  },
  lastMessageTime: {
    marginLeft: "auto",
    flex: "none",
    fontSize: "0.78rem",
    fontWeight: 600,
    color: theme.palette.text.secondary
  },
  contactLastMessage: {
    paddingRight: 10,
    color: theme.palette.text.secondary,
    lineHeight: 1.4,
    width: "100%",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
  },
  newMessagesCount: {
    alignSelf: "center",
    marginRight: 8,
    marginLeft: "auto"
  },
  badgeStyle: {
    color: "white",
    background: `linear-gradient(180deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
    boxShadow: "0 8px 14px rgba(18, 140, 126, 0.28)",
    fontWeight: 700
  },
  acceptButton: {
    position: "static",
    borderRadius: 10,
    padding: "3px 10px",
    minWidth: 78,
    marginLeft: 8,
    fontWeight: 700,
    fontSize: "0.79rem",
    boxShadow: "none",
    background:
      theme.palette.type === "dark"
        ? "rgba(18, 140, 126, 0.18)"
        : "rgba(18, 140, 126, 0.10)",
    color: theme.palette.primary.main,
    border: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(52, 211, 153, 0.18)"
        : "rgba(18, 140, 126, 0.18)"
    }`,
    "&:hover": {
      boxShadow: "none",
      background:
        theme.palette.type === "dark"
          ? "rgba(18, 140, 126, 0.24)"
          : "rgba(18, 140, 126, 0.14)"
    }
  },
  previewButton: {
    borderRadius: 10,
    padding: 5,
    minWidth: 34,
    minHeight: 34,
    marginLeft: 8,
    color: theme.palette.primary.main,
    border: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(52, 211, 153, 0.18)"
        : "rgba(18, 140, 126, 0.18)"
    }`,
    background:
      theme.palette.type === "dark"
        ? "rgba(18, 140, 126, 0.12)"
        : "rgba(18, 140, 126, 0.08)",
    "&:hover": {
      background:
        theme.palette.type === "dark"
          ? "rgba(18, 140, 126, 0.22)"
          : "rgba(18, 140, 126, 0.13)"
    }
  },
  closeButton: {
    borderRadius: 10,
    padding: 5,
    minWidth: 34,
    minHeight: 34,
    marginLeft: 8,
    color: "#b42318",
    border: "1px solid rgba(180, 35, 24, 0.28)",
    background: "rgba(180, 35, 24, 0.08)",
    "&:hover": {
      background: "rgba(180, 35, 24, 0.14)"
    }
  },
  ticketQueueColor: {
    flex: "none",
    width: 18,
    height: "calc(100% - 16px)",
    position: "absolute",
    top: "8px",
    left: "0%",
    borderRadius: "0 999px 999px 0",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    boxShadow:
      theme.palette.type === "dark"
        ? "inset -1px 0 0 rgba(255,255,255,0.18), 0 6px 14px rgba(2,6,23,0.20)"
        : "inset -1px 0 0 rgba(255,255,255,0.50), 0 6px 14px rgba(15,23,42,0.10)"
  },
  ticketQueueLabel: {
    writingMode: "vertical-rl",
    transform: "rotate(180deg)",
    maxHeight: "calc(100% - 14px)",
    maxWidth: 14,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontSize: "0.58rem",
    fontWeight: 800,
    letterSpacing: 0,
    lineHeight: 1,
    textShadow: "0 1px 1px rgba(0,0,0,0.22)",
    textTransform: "uppercase"
  },
  ticketNumber: {
    background:
      "linear-gradient(180deg, rgba(255,247,204,1) 0%, rgba(255,239,169,1) 100%)",
    color: "#8a6300",
    border: "1px solid rgba(226, 192, 84, 0.55)",
    borderRadius: 999,
    padding: "3px 8px",
    fontSize: "0.7rem",
    fontWeight: 700,
    flex: "none",
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7)"
  },
  contactName: {
    fontWeight: 700,
    fontSize: "0.98rem",
    color: theme.palette.text.primary,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
  },
  secondaryRow: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: 4,
    width: "100%",
    paddingRight: 8
  },
  lastMessageRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    width: "100%"
  },
  ticketMetaRow: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    width: "100%",
    minHeight: 18,
    paddingLeft: 2
  },
  connectionName: {
    fontSize: "0.73rem",
    fontWeight: 600,
    color:
      theme.palette.type === "dark"
        ? "rgba(203, 213, 225, 0.70)"
        : "rgba(71, 85, 105, 0.76)",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: "75%"
  },
  avatar: {
    width: 46,
    height: 46,
    boxShadow: "0 8px 18px rgba(15, 23, 42, 0.12)",
    border: "2px solid rgba(255,255,255,0.92)",
    background: "linear-gradient(135deg, #36a08e 0%, #1f6f5c 100%)",
    color: "#fff",
    fontWeight: 700,
    fontSize: "0.95rem"
  }
}));

const getInitials = name =>
  String(name || "?")
    .trim()
    .split(/\s+/)
    .map(part => part[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

const formatTicketNumber = ticketId => `#${String(ticketId || "").padStart(6, "0")}`;
const getConnectionName = ticket =>
  String(
    ticket?.whatsapp?.name ||
      ticket?.whatsappName ||
      ticket?.connectionName ||
      ""
  ).trim();

const getReadableTextColor = color => {
  const fallback = "#ffffff";
  const hex = String(color || "").replace("#", "").trim();
  if (!/^[0-9a-f]{6}$/i.test(hex)) return fallback;

  const red = parseInt(hex.slice(0, 2), 16);
  const green = parseInt(hex.slice(2, 4), 16);
  const blue = parseInt(hex.slice(4, 6), 16);
  const brightness = (red * 299 + green * 587 + blue * 114) / 1000;
  return brightness > 170 ? "#0f172a" : "#ffffff";
};

const getAgentName = (ticket, loggedUser, resolvedAgentName) => {
  const relationMatchesTicket =
    ticket?.user?.name &&
    (!ticket?.userId || Number(ticket.user.id || 0) === Number(ticket.userId || 0));

  if (relationMatchesTicket) return ticket.user.name;
  if (resolvedAgentName) return resolvedAgentName;
  if (ticket?.userName) return ticket.userName;
  if (ticket?.assignedUserName) return ticket.assignedUserName;
  if (ticket?.attendantName) return ticket.attendantName;
  if (ticket?.userId && Number(ticket.userId) === Number(loggedUser?.id)) {
    return loggedUser?.name || "Você";
  }
  if (ticket?.userId) return "Atendente";
  return "Sem atendente";
};

const normalizeMessagePreview = ticket => {
  const preview = String(ticket?.lastMessage || "")
    .replace(/\s+/g, " ")
    .trim();

  if (preview.toLowerCase().startsWith("[ligacao waba]")) {
    const parts = preview
      .split("|")
      .map(item => String(item || "").trim())
      .filter(Boolean);
    const dictionary = {};
    parts.forEach(part => {
      const separatorIndex = part.indexOf("=");
      if (separatorIndex <= 0) return;
      const key = part.slice(0, separatorIndex).trim().toLowerCase();
      const value = part.slice(separatorIndex + 1).trim();
      if (key) dictionary[key] = value;
    });

    const statusRaw = String(dictionary.status || "").toUpperCase();
    const directionRaw = String(dictionary.direcao || dictionary.direction || "").toUpperCase();
    const statusMap = {
      RINGING: "Chamando",
      INITIATED: "Iniciada",
      CONNECTED: "Conectada",
      ANSWERED: "Atendida",
      MISSED: "Não atendida",
      COMPLETED: "Finalizada",
      FAILED: "Falhou",
      BUSY: "Ocupado",
      REJECTED: "Recusada",
      CANCELED: "Cancelada"
    };
    const directionMap = {
      USER_INITIATED: "Recebida",
      BUSINESS_INITIATED: "Originada",
      INBOUND: "Recebida",
      OUTBOUND: "Originada"
    };

    const statusPt = statusMap[statusRaw] || statusRaw || "Atualização";
    const directionPt = directionMap[directionRaw] || directionRaw || "";
    return `Ligação WABA: ${statusPt}${directionPt ? ` (${directionPt})` : ""}`;
  }

  if (preview) {
    return preview;
  }

  const mediaType =
    ticket?.lastMessageType || ticket?.lastMessageMediaType || ticket?.mediaType || "";

  if (mediaType === "ptt" || mediaType === "audio") return "Audio recebido";
  if (mediaType === "image") return "Imagem recebida";
  if (mediaType === "video") return "Video recebido";
  if (mediaType === "document") return "Documento recebido";
  if (mediaType === "location") return "Localização recebida";
  if (mediaType === "sticker") return "Sticker recebido";

  return "Midia recebida";
};

const TicketListItem = ({ ticket }) => {
  const classes = useStyles();
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [resolvedAgentName, setResolvedAgentName] = useState("");
  const { ticketId } = useParams();
  const isMounted = useRef(true);
  const { user } = useContext(AuthContext);
  const isAdminLikeUser =
    user?.profile?.toLowerCase() === "admin" ||
    Boolean(user?.supportSession?.active) ||
    Boolean(user?.supervisorMode);
  const [allowUsersClosePendingWithoutAccept, setAllowUsersClosePendingWithoutAccept] =
    useState(isAdminLikeUser);
  const canPreviewPendingTicket =
    Boolean(user?.company?.allowPendingTicketPreview) ||
    isAdminLikeUser;
  const canClosePendingWithoutAccept =
    isAdminLikeUser || allowUsersClosePendingWithoutAccept;
  const isSupportSessionActive = Boolean(user?.supportSession?.active);
  const operationalUserId = isSupportSessionActive ? undefined : user?.id;
  const connectionName = getConnectionName(ticket);
  const queueName = String(ticket.queue?.name || "Sem fila").trim();
  const queueColor = ticket.queue?.color || "#7C7C7C";

  useEffect(() => {
    let isActive = true;

    const loadClosePendingPermission = async () => {
      if (isAdminLikeUser) {
        if (isActive) setAllowUsersClosePendingWithoutAccept(true);
        return;
      }

      if (pendingClosePermissionCache.loaded) {
        if (isActive) {
          setAllowUsersClosePendingWithoutAccept(pendingClosePermissionCache.value);
        }
        return;
      }

      if (!pendingClosePermissionCache.request) {
        pendingClosePermissionCache.request = api
          .get("/companies/current/preferences")
          .then(({ data }) => {
            const allowed = Boolean(data?.allowUsersClosePendingWithoutAccept);
            pendingClosePermissionCache.loaded = true;
            pendingClosePermissionCache.value = allowed;
            return allowed;
          })
          .catch(() => {
            pendingClosePermissionCache.loaded = true;
            pendingClosePermissionCache.value = false;
            return false;
          })
          .finally(() => {
            pendingClosePermissionCache.request = null;
          });
      }

      const allowed = await pendingClosePermissionCache.request;
      if (isActive) {
        setAllowUsersClosePendingWithoutAccept(Boolean(allowed));
      }
    };

    loadClosePendingPermission();

    return () => {
      isActive = false;
    };
  }, [isAdminLikeUser]);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    let isActive = true;
    const relationMatchesTicket =
      ticket?.user?.name &&
      (!ticket?.userId || Number(ticket.user.id || 0) === Number(ticket.userId || 0));
    const inlineName = String(
      (relationMatchesTicket ? ticket?.user?.name : "") ||
        ticket?.userName ||
        ticket?.assignedUserName ||
        ticket?.attendantName ||
        ""
    ).trim();

    if (inlineName) {
      setResolvedAgentName(inlineName);
      return () => {
        isActive = false;
      };
    }

    const assignedUserId = Number(ticket?.userId || 0);
    if (!assignedUserId) {
      setResolvedAgentName("");
      return () => {
        isActive = false;
      };
    }

    if (agentNameCache[assignedUserId]) {
      setResolvedAgentName(agentNameCache[assignedUserId]);
      return () => {
        isActive = false;
      };
    }

    const fetchUserName = async () => {
      try {
        const { data } = await api.get(`/users/${assignedUserId}`);
        const fetchedName = String(data?.name || data?.user?.name || "").trim();
        if (fetchedName) {
          agentNameCache[assignedUserId] = fetchedName;
          if (isActive) {
            setResolvedAgentName(fetchedName);
          }
        }
      } catch (err) {
        // noop: keep generic label when user lookup is unavailable
      }
    };

    fetchUserName();
    return () => {
      isActive = false;
    };
  }, [
    ticket?.id,
    ticket?.userId,
    ticket?.user?.name,
    ticket?.userName,
    ticket?.assignedUserName,
    ticket?.attendantName
  ]);

  const handleAcepptTicket = async id => {
    setLoading(true);
    try {
      const payload = { status: "open" };
      if (typeof operationalUserId !== "undefined") {
        payload.userId = operationalUserId;
      }

      const { data } = await api.put(`/tickets/${id}`, payload);

      if (data?.id && typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("ticket:accepted", {
            detail: { ticket: data }
          })
        );
      }
    } catch (err) {
      setLoading(false);
      toastError(err);
    }
    if (isMounted.current) {
      setLoading(false);
    }
    history.push(`/tickets/${id}`);
  };

  const handleSelectTicket = id => {
    history.push(`/tickets/${id}`);
  };

  // Espiar e abrir a conversa SEM consumir o "nao lida". O estado vai na URL
  // porque precisa sobreviver a um recarregamento e chegar junto se o link for
  // passado adiante.
  const handlePeekTicket = id => {
    history.push(`/tickets/${id}?peek=1`);
  };

  const handleClosePendingTicket = async id => {
    const confirmed = window.confirm(
      i18n.t("ticketsList.confirmations.closePendingWithoutAccept")
    );

    if (!confirmed) {
      return;
    }

    setLoading(true);
    try {
      const payload = { status: "closed" };
      if (typeof operationalUserId !== "undefined") {
        payload.userId = operationalUserId;
      }

      const { data } = await api.put(`/tickets/${id}`, payload);

      if (data?.id && typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("ticket:updated", {
            detail: {
              ticket: data,
              previousStatus: "pending",
              requestedStatus: "closed"
            }
          })
        );
      }
    } catch (err) {
      toastError(err);
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };

  return (
    <React.Fragment key={ticket.id}>
      <ListItem
        dense
        button
        onClick={() => {
          if (ticket.status === "pending" && !canPreviewPendingTicket) return;
          handleSelectTicket(ticket.id);
        }}
        selected={ticketId && +ticketId === ticket.id}
        className={clsx(classes.ticket, {
          [classes.pendingTicket]: ticket.status === "pending",
          [classes.pendingTicketPreviewEnabled]:
            ticket.status === "pending" && canPreviewPendingTicket
        })}
      >
        <Tooltip arrow placement="right" title={queueName}>
          <span
            style={{
              backgroundColor: queueColor,
              color: getReadableTextColor(queueColor)
            }}
            className={classes.ticketQueueColor}
          >
            <span className={classes.ticketQueueLabel}>{queueName}</span>
          </span>
        </Tooltip>

        <ListItemAvatar>
          <ContactAvatar contact={ticket?.contact} className={classes.avatar}>
            {getInitials(ticket?.contact?.name)}
          </ContactAvatar>
        </ListItemAvatar>

        <ListItemText
          disableTypography
          primary={
            <span className={classes.contactNameWrapper}>
              <Typography noWrap component="span" variant="body2" color="textPrimary">
                <span className={classes.primaryTextWrap}>
                  <span className={classes.ticketNumber}>{formatTicketNumber(ticket.id)}</span>
                  <span className={classes.contactName}>
                    {ticket.contact?.name || "Contato"}
                  </span>
                  <span className={classes.agentName}>
                    | {getAgentName(ticket, user, resolvedAgentName)}
                  </span>
                </span>
              </Typography>
              {ticket.updatedAt && (
                <Typography
                  className={classes.lastMessageTime}
                  component="span"
                  variant="body2"
                  color="textSecondary"
                >
                  {isSameDay(parseISO(ticket.updatedAt), new Date())
                    ? format(parseISO(ticket.updatedAt), "HH:mm")
                    : format(parseISO(ticket.updatedAt), "dd/MM/yyyy")}
                </Typography>
              )}
            </span>
          }
          secondary={
            <span className={classes.secondaryRow}>
              <span className={classes.lastMessageRow}>
                <Typography
                  className={classes.contactLastMessage}
                  noWrap
                  component="span"
                  variant="body2"
                  color="textSecondary"
                >
                  {normalizeMessagePreview(ticket)}
                </Typography>
                <Badge
                  className={classes.newMessagesCount}
                  badgeContent={ticket.unreadMessages}
                  classes={{
                    badge: classes.badgeStyle
                  }}
                />
              </span>
              {connectionName ? (
                <span className={classes.ticketMetaRow}>
                  <ConnectionChannelBadge whatsapp={ticket.whatsapp} size={28} />
                </span>
              ) : null}
            </span>
          }
        />

        {/* Fora do aguardando o olho vale para qualquer chamado: ler sem consumir
            o "nao lida" e util tambem no que ja tem dono - supervisor
            acompanhando, colega conferindo antes de assumir. No aguardando ele
            continua sujeito a permissao de espiar da empresa. */}
        {ticket.status !== "pending" && Number(ticket.unreadMessages || 0) > 0 && (
          <Tooltip arrow title="Espiar sem marcar como lida">
            <IconButton
              size="small"
              className={classes.previewButton}
              onClick={event => {
                event.stopPropagation();
                handlePeekTicket(ticket.id);
              }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        {ticket.status === "pending" && (
          <>
            {canPreviewPendingTicket && (
              <Tooltip arrow title="Espiar chamado">
                <IconButton
                  size="small"
                  className={classes.previewButton}
                  onClick={event => {
                    event.stopPropagation();
                    handlePeekTicket(ticket.id);
                  }}
                >
                  <VisibilityIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {canClosePendingWithoutAccept && (
              <Tooltip arrow title={i18n.t("ticketsList.buttons.closePending")}>
                <IconButton
                  size="small"
                  className={classes.closeButton}
                  onClick={event => {
                    event.stopPropagation();
                    handleClosePendingTicket(ticket.id);
                  }}
                  disabled={loading}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <ButtonWithSpinner
              color="primary"
              variant="contained"
              className={classes.acceptButton}
              size="small"
              loading={loading}
              onClick={event => {
                event.stopPropagation();
                handleAcepptTicket(ticket.id);
              }}
            >
              {i18n.t("ticketsList.buttons.accept")}
            </ButtonWithSpinner>
          </>
        )}
      </ListItem>
      <Divider variant="inset" component="li" />
    </React.Fragment>
  );
};

export default React.memo(TicketListItem);

